// customer (CRM) data hooks — the single place the UI talks to the customer API.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Customer,
  CustomerDetail,
  CustomerFieldsInput,
  CustomerListItem,
  DuplicateCandidate,
  MatchResult,
  MergeCustomersInput,
} from '@technohull/shared';
import { api } from '../lib/api.js';

export function useCustomers(includeArchived: boolean) {
  return useQuery({
    queryKey: ['customers', includeArchived],
    queryFn: () => api.get<{ customers: CustomerListItem[] }>(`/api/customers${includeArchived ? '?archived=1' : ''}`),
  });
}

export function useCustomer(id: number | null) {
  return useQuery({
    queryKey: ['customer', id],
    queryFn: () => api.get<CustomerDetail>(`/api/customers/get?id=${id}`),
    enabled: !!id,
  });
}

export function useCustomerSearch(q: string) {
  const query = q.trim();
  return useQuery({
    queryKey: ['customer-search', query],
    queryFn: () => api.get<{ customers: Customer[] }>(`/api/customers/search?q=${encodeURIComponent(query)}`),
    enabled: query.length >= 2,
    staleTime: 15_000,
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>, id?: number) {
  qc.invalidateQueries({ queryKey: ['customers'] });
  if (id) qc.invalidateQueries({ queryKey: ['customer', id] });
}

export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: CustomerFieldsInput) => api.post<{ id: number }>('/api/customers', v),
    onSuccess: () => invalidate(qc),
  });
}

export function useUpdateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: CustomerFieldsInput & { id: number }) => api.post('/api/customers/update', v),
    onSuccess: (_d, v) => invalidate(qc, v.id),
  });
}

// ── duplicate detection / review ──────────────────────────────────────────────
export interface CompareResponse {
  a: Customer | null;
  b: Customer | null;
  aOrders: number;
  bOrders: number;
  result: MatchResult;
  suggestedPrimaryId: number;
}

export function useDuplicates() {
  return useQuery({
    queryKey: ['customer-duplicates'],
    queryFn: () => api.get<{ candidates: DuplicateCandidate[] }>('/api/customers/duplicates'),
  });
}

export function useScanDuplicates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ customers: number; pairs: number; candidates: number }>('/api/customers/scan-duplicates', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customer-duplicates'] }),
  });
}

export function useDismissDuplicate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post('/api/customers/duplicates/dismiss', { id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customer-duplicates'] }),
  });
}

export function useCompareCustomers(a: number | null, b: number | null) {
  return useQuery({
    queryKey: ['customer-compare', a, b],
    queryFn: () => api.get<CompareResponse>(`/api/customers/compare?a=${a}&b=${b}`),
    enabled: !!a && !!b,
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['customers'] });
  qc.invalidateQueries({ queryKey: ['customer'] });
  qc.invalidateQueries({ queryKey: ['customer-duplicates'] });
}

export function useMergeCustomers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: MergeCustomersInput) => api.post<{ primaryId: number; merged: number; relinked: number }>('/api/customers/merge', v),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUnmergeCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: number) => api.post('/api/customers/unmerge', { eventId }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useArchiveCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post('/api/customers/archive', { id }),
    onSuccess: (_d, id) => invalidate(qc, id),
  });
}

export function useRestoreCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post('/api/customers/restore', { id }),
    onSuccess: (_d, id) => invalidate(qc, id),
  });
}
